import { applyProperties } from "~/utils/vmHelpers";

/**
 * Get all GraphQL error messages for errors without extensions.
 * @param {*} error
 * @returns Array with all error messages
 */
export function getErrorMessages(error) {
  const { graphQLErrors } = error;

  return graphQLErrors?.filter((graphQLError) => !graphQLError.extensions).map((graphQLError) => graphQLError.message) || [];
}

/**
 * Get all validation errors on a GraphQL response as flat object with array values.
 * @param {*} error
 * @returns {*} Object containing field names as keys and the values their validation errors as arrays.
 */
export const getValidationErrors = (error) => {
  const { graphQLErrors } = error;
  const errors = {};

  if (graphQLErrors) {
    graphQLErrors.forEach((graphQLError) => {
      const { validationErrors, humanNames } = graphQLError.extensions || {};

      if (humanNames) {
        errors.__humanNames = humanNames;
      }

      if (validationErrors) {
        Object.keys(validationErrors).forEach((key) => {
          errors[key] = errors[key] || [];
          errors[key] = errors[key].concat(validationErrors[key]);
        });
      }
    });
  }

  return errors;
};

/**
 * Applies errors from a GraphQL error.
 * @param target Where to put the errors
 * @param error GraphQL error
 */
export const applyValidationErrorsFromGraphQL = (target, error) => {
  const errors = getValidationErrors(error);
  const hasErrors = Object.keys(errors).length > 0;

  if (hasErrors) {
    applyProperties(target, getValidationErrors(error));

    return true;
  }
  return false;
};

export function getGraphQLErrorsByCode(error, code) {
  const { graphQLErrors } = error;

  return graphQLErrors?.filter((graphQLError) => graphQLError.extensions?.code === code) || [];
}

/**
 * Whether a rejected operation is a sign-out the server refused because there
 * was no session left to end — the outcome the caller wanted, so nothing to
 * report. The relogin modal signs out in exactly this state.
 *
 * The message is raised by Mutations::BaseMutation#ready? and pinned by
 * spec/graphql/mutations/sign_out_spec.rb.
 * @param {*} error
 * @returns {boolean}
 */
export const isRedundantSignOut = (error) =>
  error.operation?.operationName === "signOut" &&
  Boolean(error.graphQLErrors?.some((graphQLError) => graphQLError.message === "Only logged in users can run this mutation"));

/**
 * Whether a rejected operation was refused because the request carried no valid
 * session. The schema raises it from `Query.authorized?` and
 * Mutations::BaseMutation#ready? raises it for mutations, so it means the token
 * expired or is missing — not that this user lacks a permission.
 *
 * Pinned by spec/graphql/fgu_backend_schema_spec.rb and spec/graphql/mutations/sign_out_spec.rb.
 * @param {*} error
 * @returns {boolean}
 */
export const isUnauthenticated = (error) => Boolean(error.graphQLErrors?.some((graphQLError) => graphQLError.extensions?.code === "UNAUTHENTICATED_ERROR"));
